"use client";
import { FC, useEffect, useRef, useState } from "react";
import Apple from "../assets/apple";
import Android from "../assets/android";
import { CircularProgress } from "@mui/material";

type IButtonJoin = {
	title: string;
	anchor: string;
};
type IAppButton = {
	os: "android" | "ios";
};

const resolveStoreUrl = (value: string | undefined, fallback: string) => {
	const normalized = value?.trim();
	if (!normalized || normalized === "undefined" || normalized === "null") {
		return fallback;
	}
	try {
		const url = new URL(normalized);
		if (url.protocol !== "http:" && url.protocol !== "https:") {
			return fallback;
		}
	} catch {
		return fallback;
	}
	return normalized;
};

export const ButtonJoin: FC<IButtonJoin> = ({ title, anchor }) => {
	const handleClick = () => {
		window.location.hash = anchor;
	};

	return (
		<button
			className='w-[258px] h-[70px] max-desktop:w-[165px] max-desktop:h-[45px] max-tablet:w-[115px] max-tablet:h-[31px] bg-transparent border-[2px] flex items-center justify-center rounded-[15px] text-[27px] max-desktop:text-[16px] max-tablet:text-[11px] cursor-pointer hover:bg-buttonJoinHover hover:text-main transition-all duration-300 ease-in-out'
			onClick={handleClick}
		>
			<p className='font-bold'>{title}</p>
		</button>
	);
};


export const AppButton: FC<IAppButton> = ({ os }) => {
	const [isHovered, setIsHovered] = useState(false);
	const title = os === "ios" ? "DOWNLOAD ON THE" : "GET IT ON";
	const subTitle = os === "ios" ? "App Store" : "Google Play";
	const storeUrl =
		os === "ios"
			? resolveStoreUrl(
					process.env.NEXT_PUBLIC_APP_STORE_URL,
					"https://apps.apple.com/us/app/wbt-bridge/id6457486973",
				)
			: resolveStoreUrl(
					process.env.NEXT_PUBLIC_GOOGLE_PLAY_URL,
					"https://play.google.com/store/apps/details?id=com.wbtbridge.wbtbridge",
				);
	const bg = os === "ios" ? "bg-white" : "bg-black";
	const textColor = os === "ios" ? "text-black" : "text-white";
	const hoverBg = os === "ios" ? "hover:bg-black" : "hover:bg-white";
	const activeBg = os === "ios" ? "active:bg-black" : "active:bg-white";
	const hoverText = os === "ios" ? "hover:text-white" : "hover:text-black";
	const activeText = os === "ios" ? "active:text-white" : "active:text-black";

	const icon =
		os === "ios" ? (
			<Apple
				fillHover={
					isHovered
						? "fill-white transition-all duration-700"
						: "fill-black transition-all duration-700 "
				}
			/>
		) : (
			<Android
				fillHover={
					isHovered
						? "fill-black transition-all duration-700"
						: "fill-white transition-all duration-700"
				}
			/>
		);
	const [screenWidth, setScreenWidth] = useState(0);
	useEffect(() => {
		const handleResize = () => {
			setScreenWidth(window.innerWidth);
		};
		handleResize();
		window.addEventListener("resize", handleResize);

		return () => {
			window.removeEventListener("resize", handleResize);
		};
	}, []);
	const handleMouseEnter = () => {
		if (screenWidth >= 768) {
			setIsHovered(true);
		}
	};
	const handleMouseLeave = () => {
		if (screenWidth >= 768) {
			setIsHovered(false);
		}
	};
	const handleTouchStart = () => {
		setIsHovered(true);
	};

	const handleTouchEnd = () => {
		setIsHovered(false);
	};

	return (
		<a
			href={storeUrl}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
			onTouchStart={handleTouchStart}
			onTouchEnd={handleTouchEnd}
			aria-label={`Open ${subTitle}`}
			className={`w-[216px] h-[63px] max-desktop:h-auto max-tablet:h-[41px]  max-desktop:w-[142px]  flex items-center ${bg} ${
				screenWidth <= 768 && activeBg
			} ${
				screenWidth >= 768 && hoverBg
			} rounded-[15px] py-[5px] px-[9px] gap-[5px] border-[2px] ${textColor} ${
				screenWidth <= 768 && activeText
			} ${hoverText} cursor-pointer transition-all duration-300`}>
			{icon}
			<div className={`flex flex-col ml-[15px] max-desktop:ml-[6px]`}>
				<p
					className={` text-[10px] leading-[13px] max-desktop:text-[7px] max-desktop:leading-[9px] text-left max-tablet:leading-[9px]`}>
					{title}
				</p>
				<p
					className={` text-[23px] leading-[25px] max-desktop:text-[15px] max-desktop:leading-[19px] max-desktop:ml-[-2px]  max-tablet:leading-3 `}>
					{subTitle}
				</p>
			</div>
		</a>
	);
};

type IButtonSubscribe = {
	loading: boolean;
	func: () => void;
};

export const ButtonSubscribe: FC<IButtonSubscribe> = ({ func, loading }) => {
	const [parentSize, setParentSize] = useState(0);
	const parentRef = useRef<any>(null);

	useEffect(() => {
		const { clientHeight, clientWidth } = parentRef.current;

		setParentSize(Math.min(clientHeight, clientWidth));
	}, []);
	return (
		<button
			ref={parentRef}
			onClick={() => func()}
			className='h-[45px] flex items-center justify-center max-desktop:h-[27px] w-[142px] max-desktop:w-[84px] rounded-[15px] bg-greenButton transition-all duration-200 hover:border-[2px] text-white active:text-greenButton active:bg-white '>
			{loading ? (
				<CircularProgress size={0.7 * parentSize} style={{ color: "white" }} />
			) : (
				<p className='text-[18px] max-desktop:text-[12px]'>Subscribe</p>
			)}
		</button>
	);
};

type IButtonBurger = {
	menuIsOpen: boolean;
	toggle: () => void;
};

export const ButtonBurger: FC<IButtonBurger> = ({ menuIsOpen, toggle }) => {
	return (
		<div>
			<button
				className='relative group hidden max-tablet:block'
				onClick={toggle}>
				<div className='relative flex overflow-hidden items-center justify-center rounded-full w-[50px] h-[50px] transform transition-all bg-slate-700 ring-gray-300  duration-200 shadow-md'>
					<div className='flex flex-col justify-between w-[26px] h-[14px] transform transition-all duration-300 origin-center overflow-hidden'>
						<div
							className={`bg-white h-[2px] w-7 transform transition-all duration-300 origin-left ${
								menuIsOpen ? "translate-x-10" : ""
							}`}></div>
						<div
							className={`bg-white h-[2px] w-7 rounded transform transition-all duration-300 ${
								menuIsOpen ? "translate-x-10 delay-75" : ""
							} `}></div>
						<div
							className={`bg-white h-[2px] w-7 transform transition-all duration-300 origin-left ${
								menuIsOpen ? "translate-x-10 delay-150" : ""
							}`}></div>

						<div
							className={`absolute items-center justify-between transform transition-all duration-500 top-[6.5px] -translate-x-10 ${
								menuIsOpen ? "translate-x-0" : ""
							} flex w-0 ${menuIsOpen ? "w-12" : ""}`}>
							<div
								className={`absolute bg-white h-[2px] w-6 transform transition-all duration-500 rotate-0 delay-300 ${
									menuIsOpen ? "rotate-45" : ""
								}`}></div>
							<div
								className={`absolute bg-white h-[2px] w-6 transform transition-all duration-500 -rotate-0 delay-300 ${
									menuIsOpen ? "-rotate-45" : ""
								} `}></div>
						</div>
					</div>
				</div>
			</button>
		</div>
	);
};
